import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../supabaseClient';
import { formatSnapshotTime } from '../utils/dateUtils';
import { fetchWithBackendFallback } from '../utils/backendApi';

export default function WatchList({ user, onViewDetails }) {
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingItems, setDeletingItems] = useState(new Set());
  const [refreshingItems, setRefreshingItems] = useState(new Set());
  const [statusMessages, setStatusMessages] = useState({});

  const formatAnalysisTime = (seconds) => {
    if (!seconds && seconds !== 0) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const signalClass = (action) => {
    if (action === 'LONG') return 'signal-pill signal-pill-long';
    if (action === 'SHORT') return 'signal-pill signal-pill-short';
    return 'signal-pill signal-pill-wait';
  };

  const formatDelta = (value, suffix = 'pts') => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 'N/A';
    const sign = numeric > 0 ? '+' : '';
    return `${sign}${numeric.toFixed(2)} ${suffix}`;
  };

  const formatMaybeNumber = (value, decimals = 2, suffix = '') => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 'N/A';
    return `${numeric.toFixed(decimals)}${suffix}`;
  };

  useEffect(() => {
    fetchWatchlist();
  }, [user]);

  const fetchWatchlist = async () => {
    try {
      const { data, error: queryError } = await supabase
        .from('llm_analysis')
        .select('*')
        .eq('user_id', user.id)
        .order('snapshot_time', { ascending: false });

      if (queryError) throw queryError;

      const uniqueCoins = new Map();
      (data || []).forEach((item) => {
        const existing = uniqueCoins.get(item.coin);
        if (!existing) {
          uniqueCoins.set(item.coin, item);
          return;
        }

        const existingTs = new Date(existing.snapshot_time).getTime();
        const candidateTs = new Date(item.snapshot_time).getTime();
        if (candidateTs > existingTs) {
          uniqueCoins.set(item.coin, item);
        }
      });

      setWatchlist(Array.from(uniqueCoins.values()));
    } catch (watchlistError) {
      console.error('Error fetching watchlist:', watchlistError);
      setError('Failed to load watchlist');
    } finally {
      setLoading(false);
    }
  };

  const deleteFromWatchlist = async (itemId, coinName) => {
    if (!window.confirm(`Remove all ${coinName} entries from your watchlist?`)) return;

    setDeletingItems((prev) => new Set(prev).add(itemId));
    try {
      const { error: deleteError } = await supabase
        .from('llm_analysis')
        .delete()
        .eq('coin', coinName)
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;
      setWatchlist((prev) => prev.filter((item) => item.coin !== coinName));
    } catch (deleteError) {
      console.error('Error deleting from watchlist:', deleteError);
      setError('Failed to remove coin from watchlist');
    } finally {
      setDeletingItems((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const handleRefresh = async (item) => {
    const key = uuidv4();
    setRefreshingItems((prev) => new Set(prev).add(item.id));
    setStatusMessages((prev) => ({ ...prev, [item.id]: 'Starting analysis...' }));

    try {
      const { response: analyzeResponse, backendUrl } = await fetchWithBackendFallback('/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ coin: item.coin, key, user_id: user.id })
      });

      if (!analyzeResponse.ok) throw new Error('Failed to start analysis');

      const pollStatus = async () => {
        const statusResponse = await fetch(`${backendUrl}/status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ coin: item.coin, key })
        });

        if (!statusResponse.ok) throw new Error('Failed to get status');
        const statusData = await statusResponse.json();

        setStatusMessages((prev) => ({
          ...prev,
          [item.id]: statusData.progress || statusData.status
        }));

        if (statusData.status === 'complete') {
          await fetchWatchlist();
          setRefreshingItems((prev) => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
          setStatusMessages((prev) => ({ ...prev, [item.id]: 'Analysis complete' }));
          setTimeout(() => {
            setStatusMessages((prev) => {
              const next = { ...prev };
              delete next[item.id];
              return next;
            });
          }, 2500);
          return;
        }

        if (statusData.status === 'error') {
          setRefreshingItems((prev) => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
          setStatusMessages((prev) => ({ ...prev, [item.id]: `Error: ${statusData.error}` }));
          return;
        }

        setTimeout(pollStatus, 2500);
      };

      pollStatus();
    } catch (refreshError) {
      setRefreshingItems((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      setStatusMessages((prev) => ({ ...prev, [item.id]: 'Failed to refresh analysis' }));
    }
  };

  if (loading) {
    return (
      <main className="app-page">
        <section className="card">
          <div className="status-card">
            <div className="spinner" />
            <div>
              <h3>Loading watchlist</h3>
              <p>Fetching your latest tracked assets...</p>
            </div>
          </div>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className="app-page">
        <section className="error-banner">
          <h3>Watchlist Error</h3>
          <p>{error}</p>
        </section>
      </main>
    );
  }

  return (
    <main className="app-page space-y-6">
      <section className="card">
        <div className="page-head">
          <div>
            <p className="eyebrow">Portfolio</p>
            <h1 className="page-title">Watchlist</h1>
            <p className="page-subtitle">Track the latest call on each coin and rerun analysis from here.</p>
          </div>
        </div>

        {watchlist.length === 0 ? (
          <div className="insight-panel mt-4 text-center">
            <h4>Your watchlist is empty</h4>
            <p>Add a coin from the dashboard to start tracking changes over time.</p>
          </div>
        ) : (
          <div className="watch-grid mt-4">
            {watchlist.map((item) => {
              const analysisData = item.latest_sync || item;
              const majority = analysisData.majority_recommendation || item.majority_recommendation || 'WAIT';
              const currentPrice = analysisData.current_price || item.current_price;
              const confidenceDeltas = item.confidence_deltas || analysisData.confidence_deltas || {};
              const timeframeDeltas = confidenceDeltas.timeframe_trade_confidence_delta || {};
              const averageConfidence =
                analysisData.consensus_trade_confidence ??
                item.consensus_trade_confidence ??
                confidenceDeltas.latest_average_trade_confidence;
              const overallDelta =
                item.consensus_trade_confidence_delta ??
                confidenceDeltas.overall_trade_confidence_delta;
              const analysisTelemetry = item.analysis_telemetry || analysisData.analysis_telemetry || {};
              const modelTelemetry = item.model_telemetry || analysisData.model_telemetry || {};
              const modelCount = Object.keys(modelTelemetry).length;
              const modelTelemetryRows = Object.entries(modelTelemetry);

              return (
                <article key={item.id} className="watch-card">
                  <div className="watch-head">
                    <h3>{item.coin}</h3>
                    <div className="pill-row">
                      <span className={signalClass(majority)}>{majority}</span>
                      <span className="soft-pill">Consensus</span>
                    </div>
                  </div>

                  {analysisData.timeframe_analyses?.length > 0 ? (
                    <div className="watch-timeframes">
                      {analysisData.timeframe_analyses.map((tfData, idx) => {
                        const consensus = tfData.consensus || {};
                        const tfSignal = consensus.recommendation || tfData.majority_recommendation || 'WAIT';
                        const tfDelta = timeframeDeltas[tfData.timeframe];
                        return (
                          <div key={`${item.id}-${idx}`} className="tf-chip-row">
                            <span className="soft-pill">{tfData.timeframe}</span>
                            <span className={signalClass(tfSignal)}>{tfSignal}</span>
                            <span className="soft-pill">{consensus.trade_confidence ?? 0}% conf</span>
                            {tfDelta !== null && tfDelta !== undefined && (
                              <span className="soft-pill">{formatDelta(tfDelta)}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="insight-panel compact">
                      <h4>No timeframe data</h4>
                      <p>This snapshot doesn't include timeframe data. Refresh to run it again.</p>
                    </div>
                  )}

                  {modelTelemetryRows.length > 0 && (
                    <div className="watch-timeframes">
                      {modelTelemetryRows.map(([modelName, modelStats]) => (
                        <div key={`${item.id}-${modelName}`} className="tf-chip-row">
                          <span className="soft-pill">{modelName}</span>
                          <span className="soft-pill">{formatMaybeNumber(modelStats.avg_latency_ms, 0, ' ms')}</span>
                          <span className="soft-pill">{formatMaybeNumber((modelStats.failure_rate || 0) * 100, 2, '% fail')}</span>
                          <span className="soft-pill">{modelStats.cost_usd !== null && modelStats.cost_usd !== undefined ? `$${Number(modelStats.cost_usd).toFixed(6)}` : 'N/A cost'}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="watch-meta">
                    <p>Last Updated: {formatSnapshotTime(item.snapshot_time, true)}</p>
                    <p>Analysis Time: {formatAnalysisTime(item.analysis_time)}</p>
                    {currentPrice && <p>Current Price: ${currentPrice}</p>}
                    <>
                      <p>Avg Trade Confidence: {formatMaybeNumber(averageConfidence, 2, '%')}</p>
                      <p>Confidence Delta: {formatDelta(overallDelta)}</p>
                      <p>Model Failure Rate: {formatMaybeNumber((analysisTelemetry.overall_failure_rate || 0) * 100, 2, '%')}</p>
                      <p>Avg Model Latency: {formatMaybeNumber(analysisTelemetry.avg_model_latency_ms, 0, ' ms')}</p>
                      <p>Estimated Model Cost: {analysisTelemetry.estimated_total_cost_usd !== null && analysisTelemetry.estimated_total_cost_usd !== undefined ? `$${Number(analysisTelemetry.estimated_total_cost_usd).toFixed(6)}` : 'N/A'}</p>
                      <p>Model Calls: {analysisTelemetry.total_model_requests ?? modelCount}</p>
                    </>
                    {statusMessages[item.id] && <p>{statusMessages[item.id]}</p>}
                  </div>

                  <div className="watch-actions">
                    <button onClick={() => onViewDetails(item.coin, false)} className="btn-primary">
                      View Details
                    </button>
                    {item.previous_sync && (
                      <button onClick={() => onViewDetails(item.coin, true)} className="btn-secondary">
                        Previous snapshot
                      </button>
                    )}
                    <button
                      onClick={() => handleRefresh(item)}
                      className="btn-secondary"
                      disabled={refreshingItems.has(item.id)}
                    >
                      {refreshingItems.has(item.id) ? 'Refreshing...' : 'Refresh'}
                    </button>
                    <button
                      onClick={() => deleteFromWatchlist(item.id, item.coin)}
                      className="btn-secondary"
                      disabled={deletingItems.has(item.id)}
                    >
                      {deletingItems.has(item.id) ? 'Removing...' : 'Remove'}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
